import type { MetricEvent } from '@/lib/metrics';

/**
 * Every funnel event goes through here so call sites stay one line and the
 * destinations can change without touching them.
 *
 * Two destinations on purpose: GA4 is already loaded and is useful once volume
 * grows, but it thresholds low counts, so the first-party counter is what the
 * admin funnel page actually reads.
 */

type Gtag = (command: 'event', event: string, params?: Record<string, unknown>) => void;

function gtag(): Gtag | null {
  if (typeof window === 'undefined') return null;
  const fn = (window as unknown as { gtag?: Gtag }).gtag;
  return typeof fn === 'function' ? fn : null;
}

export interface TrackOptions {
  /** The one dimension worth splitting this event by: trigger, plan, source. */
  variant?: string;
  /** Extra context for GA4 only. Not stored in the first-party counters. */
  params?: Record<string, unknown>;
}

export function track(event: MetricEvent, options: TrackOptions = {}): void {
  const { variant, params } = options;

  gtag()?.('event', event, { ...(variant ? { variant } : {}), ...params });

  // Fire and forget: instrumentation must never block or break the interaction.
  try {
    void fetch('/api/metrics', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event, variant }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Ignore.
  }
}

/** Buckets a running count so the stored variant stays a bounded set. */
export function countBucket(count: number): string {
  if (count <= 1) return 'n_1';
  if (count <= 5) return 'n_2_5';
  if (count <= 15) return 'n_6_15';
  if (count <= 30) return 'n_16_30';
  if (count <= 60) return 'n_31_60';
  return 'n_60_plus';
}
